import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Button, Tabs, Tab, Box, Typography } from '@mui/material';
import HelpIcon from './HelpIcon';


const STUDY_LABELS = [
  'Notes',
  'Calendar',
  'Cycle Times'
];

const Highlight = ({ text, find }) => {
  if (!find) return text;
  const regex = new RegExp(`(${find})`, 'i');
  const parts = text.split(regex);
  let found = false;
  return (
    <>
      {parts.map((part, i) => {
        if (!found && part.toLowerCase() === find.toLowerCase()) {
          found = true;
          return <span key={i} id="first-match" style={{ backgroundColor: 'yellow' }}>{part}</span>;
        }
        return part;
      })}
    </>
  );
};

Highlight.propTypes = {
  text: PropTypes.string.isRequired,
  find: PropTypes.string.isRequired,
};


export default function TenTabStrategyDialog({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [notes, setNotes] = useState("");
  const [calendarText, setCalendarText] = useState('');
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [calendarSearch, setCalendarSearch] = useState('');
  const [calendarFind, setCalendarFind] = useState('');

  // Load calendar text when Calendar tab is opened for the first time
  React.useEffect(() => {
    if (tab === 1 && !calendarLoaded) {
      fetch('/calendar.txt')
        .then(res => res.ok ? res.text() : '')
        .then(text => {
          setCalendarText(text);
          setCalendarLoaded(true);
        });
    }
  }, [tab, calendarLoaded]);

  const handleFind = () => {
    const firstMatchElement = document.getElementById('first-match');
    if (firstMatchElement) {
      firstMatchElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{flexGrow: 1, textAlign: 'center', paddingLeft: '48px'}}>Notes and Reference</span>
        <HelpIcon helpKey="notesReferenceDialog" />
      </DialogTitle>
      <DialogContent sx={{ minHeight: 620, paddingBottom: 2 }}>
        <Box display="flex" gap={3}>
          {/* Left: Tabs and Study Forms */}
          <Box flex={2}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
  {STUDY_LABELS.map((label) => (
    <Tab key={label} label={label} />
  ))}
</Tabs>
            <Box sx={{ flex: 1, minHeight: 300, p: 2, border: '1px solid #ddd', borderRadius: 1, background: '#fafaff', display: 'flex', flexDirection: 'column' }}>
  {tab === 0 && (
    <>
      <textarea
        style={{ flex: 1, width: '100%', minHeight: 480, resize: 'vertical', fontSize: 16, padding: 8, marginBottom: 24 }}
        placeholder="Jot down your thoughts or ideas here..."
        value={notes}
        onChange={e => setNotes(e.target.value)}
      />
    </>
  )}
  {tab === 2 && (
    <Box display="flex" flexDirection="column" alignItems="center" height="100%" sx={{ overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Cycle Information</Typography>
        <img src="/images/cycles-data.png" alt="Cycle Information" style={{ marginTop: '16px', border: '1px solid #ccc', borderRadius: '8px' }} />
    </Box>
  )}
  {tab === 1 && (
        calendarLoaded ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="text"
                  placeholder="Find..."
                  value={calendarFind}
                  onChange={e => setCalendarFind(e.target.value)}
                  style={{ padding: '6px 10px', fontSize: 16, borderRadius: 6, border: '1px solid #bbb', outline: 'none', width: 150 }}
                />
                <Button onClick={handleFind} variant="contained" size="small">Find</Button>
              </Box>
              <input
                type="text"
                placeholder="Filter..."
                value={calendarSearch}
                onChange={e => setCalendarSearch(e.target.value)}
                style={{ padding: '6px 10px', fontSize: 16, borderRadius: 6, border: '1px solid #bbb', outline: 'none', width: 150 }}
              />
            </Box>
            <Box sx={{ maxHeight: 480, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 2, p: 0, background: '#fff' }}>
              {
                (calendarText.match(/={25,}\n[\s\S]*?(?=={25,}\n|$)/g) || [])
                  .filter(block =>
                    !calendarSearch.trim() || block.toLowerCase().includes(calendarSearch.trim().toLowerCase())
                  )
                  .map((block, idx) => (
                    <Box key={idx} sx={{
                      background: idx % 2 === 0 ? '#f5f7fa' : '#e3ecf7',
                      p: 2,
                      borderBottom: '1px solid #e0e0e0',
                      fontFamily: 'monospace',
                      fontSize: 20,
                      whiteSpace: 'pre-line',
                    }}>
                      <Highlight text={block.trim()} find={calendarFind} />
                    </Box>
                  ))
              }
            </Box>
          </>
        ) : (
          <Box sx={{ minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>Loading calendar...</Box>
        )
      )}
</Box>
          </Box>
          {/* Removed right-side list box and controls as requested */}
        </Box>
            <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </Box>
    </DialogContent>
  </Dialog>
  );
}

TenTabStrategyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
